import React, { useState, useEffect, useRef } from 'react';
import { Search, Loader2, Users, Briefcase, Building2, ChevronRight, X, User as UserIcon } from 'lucide-react';
import { supabase, supabase_get_authorized_client_ids, pickClientCompanyNumber } from '../services/supabaseService';
import { User, UserRole } from '../types';
import { searchDbIlikePattern, searchTextIncludesAny } from '../utils/searchText';

interface SearchResult {
  id: string;
  type: 'vacancy' | 'candidate' | 'client' | 'contact';
  title: string;
  subtitle?: string;
  logoUrl?: string;
}

interface GlobalSearchProps {
  user: User;
  isMobile?: boolean;
  className?: string;
}

const VACANCY_SEARCH_SELECT =
  'id, title, status, data, vacancy_number, eindklant:clients!vacancies_eindklant_id_fkey(name), opdrachtgever:clients!vacancies_opdrachtgever_id_fkey(name)';

const CANDIDATE_SEARCH_SELECT = 'id, name, email, phone, status, candidate_number';

const CLIENT_SEARCH_SELECT = 'id, name, email, phone, address_city, logo_url, invoice_kvk, data, bullhorn_id';

/** "#3653" → "3653" voor nummer-zoekopdrachten */
const stripLeadingHash = (q: string) => q.trim().replace(/^#+/, '');

const parseNumericSearchTerm = (q: string): number => {
  const s = stripLeadingHash(q);
  if (!/^\d+$/.test(s)) return NaN;
  const n = parseInt(s, 10);
  return Number.isFinite(n) ? n : NaN;
};

export default function GlobalSearch({ user, isMobile = false, className = '' }: GlobalSearchProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Close on Escape or outside click
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setIsOpen((prev) => {
          if (!prev) setTimeout(() => inputRef.current?.focus(), 50);
          return !prev;
        });
      }
      if (e.key === 'Escape') {
        setIsOpen(false);
        setQuery('');
      }
    };

    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (!query.trim()) {
      setResults([]);
      setIsLoading(false);
      return;
    }

    const timer = setTimeout(async () => {
      setIsLoading(true);
      try {
        const trimmedQuery = query.trim();
        const matchQuery = stripLeadingHash(trimmedQuery);
        const numericId = parseNumericSearchTerm(trimmedQuery);
        const isNumericOnly = !Number.isNaN(numericId);
        const dbPattern = searchDbIlikePattern(matchQuery);
        let allowedClientIds: string[] = [];

        if (user.role === UserRole.CLIENT || user.role === UserRole.CLIENT_RECRUITER) {
          allowedClientIds = await supabase_get_authorized_client_ids(user.clientId);
        }

        // Safe chunk size to avoid 400 URL Too Long from PostgREST (Max 20 IDs)
        const CHUNK_SIZE = 20;
        const chunks = [];
        if (allowedClientIds.length > 0) {
            for (let i = 0; i < allowedClientIds.length; i += CHUNK_SIZE) {
               chunks.push(allowedClientIds.slice(i, i + CHUNK_SIZE));
            }
        } else {
            chunks.push([]);
        }

        const safeSearchTerm = dbPattern.replace(/"/g, ''); // strip double quotes

        const queryPromises = [];

        for (const chunk of chunks) {
            const withVacancyScope = (q: any) => (chunk.length > 0 ? q.in('client_id', chunk) : q);
            const withCandidateScope = (q: any) => (chunk.length > 0 ? q.in('client_id', chunk) : q);
            const withClientScope = (q: any) => (chunk.length > 0 ? q.in('id', chunk) : q);
            const withContactScope = (q: any) => (chunk.length > 0 ? q.in('linked_client_id', chunk) : q);
            // Vacancies — vacancy_number is integer; no ilike on number columns
            if (!isNumericOnly) {
              queryPromises.push((async () => {
                let q = supabase.from('vacancies').select(VACANCY_SEARCH_SELECT).ilike('title', dbPattern).limit(20);
                q = withVacancyScope(q);
                const res = await q;
                if (res.error) console.error('Vacancy search error', res.error);
                return { type: 'vacancies', res };
              })());
            }
            if (!Number.isNaN(numericId)) {
              queryPromises.push((async () => {
                let q = supabase.from('vacancies').select(VACANCY_SEARCH_SELECT).eq('vacancy_number', numericId).limit(5);
                q = withVacancyScope(q);
                const res = await q;
                if (res.error) console.error('Vacancy number search error', res.error);
                return { type: 'vacancies', res };
              })());
            }

            // Candidates
            if (!isNumericOnly) {
              queryPromises.push((async () => {
                let q = supabase
                  .from('candidates')
                  .select(CANDIDATE_SEARCH_SELECT)
                  .or(`name.ilike."${safeSearchTerm}",email.ilike."${safeSearchTerm}"`)
                  .limit(20);
                q = withCandidateScope(q);
                const res = await q;
                if (res.error) console.error('Candidate search error', res.error);
                return { type: 'candidates', res };
              })());
            }
            if (!Number.isNaN(numericId)) {
              queryPromises.push((async () => {
                let q = supabase.from('candidates').select(CANDIDATE_SEARCH_SELECT).eq('candidate_number', numericId).limit(5);
                q = withCandidateScope(q);
                const res = await q;
                if (res.error) console.error('Candidate number search error', res.error);
                return { type: 'candidates', res };
              })());
            }

            // Clients
            if (!isNumericOnly) {
              queryPromises.push((async () => {
                let q = supabase.from('clients').select(CLIENT_SEARCH_SELECT).ilike('name', dbPattern).limit(20);
                q = withClientScope(q);
                const res = await q;
                if (res.error) console.error('Client search error', res.error);
                return { type: 'clients', res };
              })());
            }
            if (!Number.isNaN(numericId)) {
              queryPromises.push((async () => {
                const numStr = String(numericId);
                let q = supabase
                  .from('clients')
                  .select(CLIENT_SEARCH_SELECT)
                  .or(
                    `invoice_kvk.eq.${numStr},bullhorn_id.eq.${numStr},data->>client_number.eq.${numStr},data->>company_number.eq.${numStr}`,
                  )
                  .limit(5);
                q = withClientScope(q);
                const res = await q;
                if (res.error) console.error('Client number search error', res.error);
                return { type: 'clients', res };
              })());
            }

            // Contacts (text only)
            if (!isNumericOnly) {
              queryPromises.push((async () => {
                let q = supabase
                  .from('client_contacts')
                  .select(
                    'id, first_name, last_name, email, phone, client_id, linked_client_id, linked_org:clients!client_contacts_linked_client_id_fkey(name, logo_url)',
                  )
                  .or(`first_name.ilike."${safeSearchTerm}",last_name.ilike."${safeSearchTerm}",email.ilike."${safeSearchTerm}"`)
                  .limit(20);
                q = withContactScope(q);
                const res = await q;
                if (res.error) console.error('Contact search error', res.error);
                return { type: 'contacts', res };
              })());
            }
        }

        const allChunksCompleted = await Promise.all(queryPromises);
        
        let vacanciesRes: any = { data: [] };
        let candidatesRes: any = { data: [] };
        let clientsRes: any = { data: [] };
        let contactsRes: any = { data: [] };
        
        for (const c of allChunksCompleted) {
            if (c.res.data) {
                if (c.type === 'vacancies') vacanciesRes.data.push(...c.res.data);
                if (c.type === 'candidates') candidatesRes.data.push(...c.res.data);
                if (c.type === 'clients') clientsRes.data.push(...c.res.data);
                if (c.type === 'contacts') contactsRes.data.push(...c.res.data);
            }
        }

        const dedupeById = <T extends { id: string }>(rows: T[]) => {
          const seen = new Set<string>();
          return rows.filter((r) => {
            if (seen.has(r.id)) return false;
            seen.add(r.id);
            return true;
          });
        };

        const matchesNumericId = (stored: string | number | null | undefined) => {
          if (Number.isNaN(numericId)) return false;
          const s = String(stored ?? '').replace(/^#+/, '');
          return s === String(numericId) || parseInt(s, 10) === numericId;
        };

        vacanciesRes.data = dedupeById(vacanciesRes.data)
          .filter((v: any) =>
            isNumericOnly
              ? matchesNumericId(v.vacancy_number)
              : searchTextIncludesAny(
                  matchQuery,
                  v.title,
                  v.eindklant?.name,
                  v.opdrachtgever?.name,
                  String(v.vacancy_number ?? ''),
                  v.vacancy_number != null ? `#${v.vacancy_number}` : '',
                ),
          )
          .slice(0, 5);
        candidatesRes.data = dedupeById(candidatesRes.data)
          .filter((c: any) =>
            isNumericOnly
              ? matchesNumericId(c.candidate_number)
              : searchTextIncludesAny(
                  matchQuery,
                  c.name,
                  c.email,
                  c.phone,
                  String(c.candidate_number ?? ''),
                  c.candidate_number != null ? `#${c.candidate_number}` : '',
                ),
          )
          .slice(0, 5);
        clientsRes.data = dedupeById(clientsRes.data)
          .filter((c: any) => {
            const companyNo = pickClientCompanyNumber(c);
            return isNumericOnly
              ? matchesNumericId(companyNo) || matchesNumericId(c.bullhorn_id)
              : searchTextIncludesAny(
                  matchQuery,
                  c.name,
                  c.email,
                  c.address_city,
                  companyNo,
                  companyNo ? `#${companyNo}` : '',
                  c.bullhorn_id != null ? String(c.bullhorn_id) : '',
                );
          })
          .slice(0, 5);
        contactsRes.data = dedupeById(contactsRes.data)
          .filter((c: any) =>
            searchTextIncludesAny(
              matchQuery,
              c.first_name,
              c.last_name,
              `${c.first_name || ''} ${c.last_name || ''}`,
              c.email,
              (c.linked_org as any)?.name,
            ),
          )
          .slice(0, 5);

        const combinedResults: SearchResult[] = [];

        if (vacanciesRes.data) {
          combinedResults.push(
            ...vacanciesRes.data.map((v: any) => {
              const eindklantName = v.eindklant?.name;
              const opdrachtgeverName = v.opdrachtgever?.name;
              const location = v.data?.location || v.data?.plaats;
              const werkplek = v.data?.werkplek;
              const displayStatus = v.data?.lifecycleStatus || v.status;

              const subtitleParts = [
                eindklantName && `Eindklant: ${eindklantName}`,
                opdrachtgeverName && `Opdrachtgever: ${opdrachtgeverName}`,
                location,
                werkplek,
                displayStatus
              ].filter(Boolean);

              const titleStr = v.vacancy_number ? `#${v.vacancy_number} - ${v.title || 'Onbekende Vacature'}` : v.title || 'Onbekende Vacature';

              return {
                id: v.id,
                type: 'vacancy' as const,
                title: titleStr,
                subtitle: subtitleParts.join(' • ') || 'Geen extra info'
              };
            })
          );
        }

        if (candidatesRes.data) {
          combinedResults.push(
            ...candidatesRes.data.map((c: any) => {
              const titleStr = c.candidate_number ? `#${c.candidate_number} - ${c.name || 'Onbekende Kandidaat'}` : c.name || 'Onbekende Kandidaat';
              return {
                id: c.id,
                type: 'candidate' as const,
                title: titleStr,
                subtitle: [c.phone, c.email, c.status].filter(Boolean).join(' • ') || 'Geen extra info'
              };
            })
          );
        }

        if (clientsRes.data) {
          combinedResults.push(
            ...clientsRes.data.map((c: any) => {
              const companyNo = pickClientCompanyNumber(c);
              const titleStr = companyNo
                ? `#${companyNo} - ${c.name || 'Onbekende Bedrijf'}`
                : c.name || 'Onbekende Bedrijf';
              return {
                id: c.id,
                type: 'client' as const,
                title: titleStr,
                subtitle: [c.email, c.phone, c.address_city].filter(Boolean).join(' • ') || 'Bedrijf',
                logoUrl: c.logo_url
              };
            })
          );
        }

        if (contactsRes.data) {
          combinedResults.push(
            ...contactsRes.data.map((c: any) => {
              const titleStr = `${c.first_name || ''} ${c.last_name || ''}`.trim() || 'Onbekende Contactpersoon';
              return {
                id: c.id,
                type: 'contact' as const,
                title: titleStr,
                subtitle: [(c.linked_org as any)?.name, c.email, c.phone].filter(Boolean).join(' • ') || 'Contactpersoon',
                logoUrl: (c.linked_org as any)?.logo_url
              };
            })
          );
        }

        setResults(combinedResults);
      } catch (e) {
        console.error('Search error:', e);
      } finally {
        setIsLoading(false);
      }
    }, 300); // 300ms debounce

    return () => clearTimeout(timer);
  }, [query, user]);

  const handleSelect = (result: SearchResult) => {
    setIsOpen(false);
    setQuery('');

    if (result.type === 'vacancy') {
      window.dispatchEvent(new CustomEvent('open-vacancy', { detail: result.id }));
    } else if (result.type === 'candidate') {
      window.dispatchEvent(new CustomEvent('open-candidate', { detail: result.id }));
    } else if (result.type === 'client' || result.type === 'contact') {
      window.dispatchEvent(new CustomEvent('open-client', { detail: result.id }));
    }
  };

  const IconForType = (type: string) => {
    switch (type) {
      case 'vacancy': return <Briefcase size={16} className="text-[#e51b23]" />;
      case 'candidate': return <Users size={16} className="text-blue-500" />;
      case 'client': return <Building2 size={16} className="text-indigo-500" />;
      case 'contact': return <UserIcon size={16} className="text-emerald-500" />;
      default: return <Search size={16} />;
    }
  };

  if (isMobile) {
    return (
      <div className="relative z-50">
        <button
          onClick={() => { setIsOpen(!isOpen); setTimeout(() => inputRef.current?.focus(), 50); }}
          className="p-2 text-slate-600 hover:text-slate-900 transition-colors"
        >
          <Search size={22} />
        </button>

        {isOpen && (
          <div className="fixed top-16 left-0 right-0 p-4 bg-white/95 backdrop-blur-md border-b border-slate-100 shadow-xl animate-slideDown" ref={containerRef}>
            <div className="relative">
              <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                ref={inputRef}
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Zoek vacatures, kandidaten..."
                className="w-full pl-10 pr-10 py-3 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:border-[#e51b23] focus:ring-2 focus:ring-[#e51b23]/20 transition-all font-medium text-slate-900"
              />
              {isLoading && (
                <Loader2 size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 animate-spin" />
              )}
            </div>

            {query && (
              <div className="mt-3 bg-white rounded-xl border border-slate-100 shadow-sm max-h-[300px] overflow-y-auto">
                {results.length > 0 ? (
                  results.map((res) => (
                    <button
                      key={res.id}
                      onClick={() => handleSelect(res)}
                      className="w-full flex items-center gap-3 p-3 text-left border-b border-slate-50 last:border-0 hover:bg-slate-50 transition-colors"
                    >
                      <div className="w-8 h-8 rounded-lg bg-slate-50 flex items-center justify-center shrink-0 border border-slate-100">
                        {IconForType(res.type)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-bold text-slate-900 truncate">{res.title}</p>
                        {res.subtitle && <p className="text-[11px] font-medium text-slate-500 truncate mt-0.5">{res.subtitle}</p>}
                      </div>
                      <ChevronRight size={14} className="text-slate-300" />
                    </button>
                  ))
                ) : !isLoading ? (
                  <div className="p-6 text-center text-sm font-medium text-slate-500">
                    Geen resultaten gevonden voor "{query}"
                  </div>
                ) : null}
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={`relative ${className}`} ref={containerRef}>
      <div
        className={`flex items-center bg-white/60 backdrop-blur-md border ${isOpen || query ? 'border-[#e51b23] ring-4 ring-[#e51b23]/10' : 'border-slate-200 hover:border-slate-300'} rounded-xl transition-all shadow-sm group`}
        onClick={() => inputRef.current?.focus()}
      >
        <div className="pl-3 py-2 text-slate-400 group-hover:text-[#e51b23] transition-colors">
          <Search size={16} />
        </div>
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => setIsOpen(true)}
          placeholder="Zoek alles (Cmd/Ctrl+K)"
          className="w-64 bg-transparent outline-none px-3 py-2 text-sm font-medium text-slate-900 placeholder:text-slate-400 focus:w-80 transition-all duration-300"
        />
        <div className="pr-3 py-2 flex items-center justify-center gap-2">
          {isLoading ? (
            <Loader2 size={14} className="text-slate-400 animate-spin" />
          ) : query ? (
            <button onClick={() => { setQuery(''); inputRef.current?.focus(); }} className="text-slate-400 hover:text-slate-600 bg-slate-100 rounded-full p-0.5">
              <X size={12} />
            </button>
          ) : (
            <div className="hidden lg:flex items-center gap-0.5 text-[10px] font-bold text-slate-400 bg-slate-100/80 px-1.5 py-0.5 rounded border border-slate-200 shadow-sm">
              <span>⌘</span><span>K</span>
            </div>
          )}
        </div>
      </div>

      {isOpen && query && (
        <div className="absolute top-full left-0 mt-2 w-full lg:w-[400px] bg-white border border-slate-100 rounded-2xl shadow-[0_10px_40px_-10px_rgba(0,0,0,0.15)] overflow-hidden z-[999] animate-slideDown">
          <div className="max-h-[70vh] overflow-y-auto no-scrollbar">
            {results.length > 0 ? (
              <div className="p-2 space-y-1">
                <div className="px-3 py-2">
                  <span className="text-[10px] font-black uppercase tracking-widest text-slate-400">Resultaten</span>
                </div>
                {results.map((res) => (
                  <button
                    key={`${res.type}-${res.id}`}
                    onClick={() => handleSelect(res)}
                    className="w-full flex items-center gap-3 p-3 text-left rounded-xl hover:bg-slate-50 transition-all border border-transparent hover:border-slate-100 active:scale-[0.98] group"
                  >
                    <div className="w-10 h-10 rounded-xl bg-slate-50 flex items-center justify-center shrink-0 border border-slate-200 shadow-sm group-hover:bg-white transition-colors overflow-hidden">
                      {res.logoUrl ? (
                        <img src={res.logoUrl} alt="" className="w-full h-full object-contain p-1" />
                      ) : (
                        IconForType(res.type)
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-bold text-slate-900 truncate group-hover:text-[#e51b23] transition-colors">{res.title}</p>
                      {res.subtitle && <p className="text-xs font-medium text-slate-500 truncate mt-0.5">{res.subtitle}</p>}
                    </div>
                    <ChevronRight size={16} className="text-slate-300 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all" />
                  </button>
                ))}
              </div>
            ) : !isLoading ? (
              <div className="p-8 flex flex-col items-center justify-center gap-3">
                <div className="w-12 h-12 bg-slate-50 rounded-full flex items-center justify-center text-slate-300 border border-slate-100">
                  <Search size={24} />
                </div>
                <div className="text-center">
                  <p className="text-sm font-bold text-slate-700">Geen resultaten gevonden</p>
                  <p className="text-xs text-slate-500 mt-1">Probeer een andere zoekterm.</p>
                </div>
              </div>
            ) : null}
          </div>
        </div>
      )}
    </div>
  );
}
